import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Footer } from './components/common/page/Footer';
import { Auth } from './components/auth/Auth';
import { Vote } from './components/vote/Vote';
import { LoadingPanel } from './components/common/LoadingPanel';
import { ErrorBanner } from './components/common/ErrorBanner';
import { VoterValidateResult } from './types/voteTypes';
import { useVoting } from './contexts/VotingContext';

import './telerik.css'
import './tailwind.css';
import './custom.css';

export default function VoteApp() {
    // Auth states
    const [isAuthInit, setIsAuthInit] = useState(false);
    const [isAuthLogin, setIsAuthLogin] = useState(false);
    const [isAuthReceipt, setIsAuthReceipt] = useState(false);
    const [isAuthPersonalId, setIsAuthPersonalId] = useState(false);
    const [authError, setAuthError] = useState(null);

    // Authenticated voter/proxy
    const { voter, setVoter, ballot, setBallot, setAdmin, previewId, setPreviewId, isPreview, setPreview } = useVoting();

    const [validateState, setValidateState] = useState(null);
    //const [isValidatedAdmin, setIsValidatedAdmin] = useState(false);

    // Auth inputs
    const [voterId, setVoterId] = useState(null);
    const [voterGuid, setVoterGuid] = useState(null);
    const [ballotId, setBallotId] = useState(null);
    const [receipt, setReceipt] = useState(null);
    const [personalId, setPersonalId] = useState(null);
    const [bioId, setBioId] = useState(null);
    const [shortCode, setShortCode] = useState(null);

    // Views
    const [isInfoPageUrl, setIsInfoPageUrl] = useState(false);

    // Page
    const [pageError, setPageError] = useState(null);

    useEffect(() => {
        const path = window.location.pathname;
        const pathParts = path.split("/");

        let shortCode = null;
        let ballotId = null;
        let previewId = null;
        let bioId = null;
        let proxyId = null;
        let voterId = null;
        let voterGuid = null;
        let receipt = null;
        let personalId = null;

        if (pathParts[1] === "v") {
            shortCode = pathParts[2] === "i" ? pathParts[3] : pathParts[2];
            setShortCode(shortCode);
        } else {
            const urlParams = new URLSearchParams(window.location.search);
            ballotId = urlParams.get('b');
            previewId = urlParams.get('preview');
            bioId = urlParams.get('bio');
            proxyId = urlParams.get('proxy');
            voterId = urlParams.get('id');
            voterGuid = urlParams.get('key');
            receipt = urlParams.get('receipt');
            personalId = urlParams.get('pId');

            setBallotId(ballotId);
            setPreviewId(previewId);
            setBioId(bioId);
            setVoterId(voterId);
            setVoterGuid(voterGuid);
            setReceipt(receipt);
            setPersonalId(personalId);
        }

        if (pathParts[1] === "info" || pathParts[2] === "i") {
            setIsInfoPageUrl(true);
        }

        if (voterId && voterGuid) {
            authVoter(voterId, voterGuid, receipt, personalId, previewId);
        } else if (shortCode) {
            authShortCode(shortCode);
        } else if (ballotId && previewId) {
            authPreview(ballotId, previewId);
        } else if (ballotId && proxyId) {
            authProxy(ballotId, proxyId);
        } else if (ballotId) {
            getBallotForLogin(ballotId);
        } else {
            setIsAuthInit(true);
            setIsAuthLogin(true);
        }
    }, []);

    const getBallotForLogin = (ballotId) => {
        axios.post('/auth/ballot', { ballotId })
            .then(response => {
                if (response.data.isSuccessful) {
                    setBallot(response.data.payload);
                } else {
                    setAuthError(response.data.errorMessage);
                }
                setIsAuthInit(true);
                setIsAuthLogin(true);
            })
            .catch(alert);
    };

    const authVoter = (voterId, voterGuid, receipt, personalId, previewId, onComplete) => {
        axios.post('/auth/voter', { id: voterId, key: voterGuid, receipt, personalId, previewId })
            .then(response => {
                if (response.data.isSuccessful) {
                    handleAuthResponse(response.data.payload);
                } else {
                    setAuthError(response.data.errorMessage);
                    setIsAuthInit(true);
                    setIsAuthLogin(true);
                }
                if (onComplete) onComplete(response.data.payload?.validateState);
            })
            .catch(alert);
    };

    const authProxy = (ballotId, proxyId) => {
        axios.post('/auth/proxy', { ballotId, proxy: proxyId })
            .then(response => {
                if (response.data.isSuccessful) {
                    handleAuthResponse(response.data.payload);
                } else {
                    setPageError(response.data.errorMessage);
                    setIsAuthInit(true);
                }
            })
            .catch(alert);
    };

    const authPreview = (ballotId, previewId, onComplete) => {
        axios.post('/auth/preview', { ballotId: parseInt(ballotId), previewId })
            .then(response => {
                if (response.data.isSuccessful) {
                    setPreview(true);
                    handleAuthResponse(response.data.payload);
                } else {
                    setPageError(response.data.errorMessage);
                    setIsAuthInit(true);
                }
                if (onComplete) onComplete();
            })
            .catch(alert);
    };

    const authShortCode = (shortCode, onComplete) => {
        axios.post('/auth/short', { shortCode })
            .then(response => {
                if (response.data.isSuccessful) {
                    const authView = response.data.payload;
                    setVoterId(authView.voter.uniqueId);
                    setVoterGuid(authView.voter.guid);
                    handleAuthResponse(authView);
                } else {
                    setPageError(response.data.errorMessage);
                    setIsAuthInit(true);
                }
                if (onComplete) onComplete();
            })
            .catch(alert);
    };

    const handleAuthResponse = (authView) => {
        const { voter, ballot, validateState, isValidatedAdmin } = authView;

        document.title = ballot.name;

        setVoter(voter);
        setBallot(ballot);
        setAdmin(isValidatedAdmin);
        setValidateState(validateState);

        setIsAuthReceipt(
            validateState === VoterValidateResult.ReceiptRequired ||
            validateState === VoterValidateResult.ReceiptInvalid
        );
        setIsAuthPersonalId(
            validateState === VoterValidateResult.PersonalIdRequired ||
            validateState === VoterValidateResult.PersonalIdInvalid
        );
        setIsAuthLogin(false);

        let error = null;
        if (validateState === VoterValidateResult.ReceiptInvalid) {
            error = "The receipt supplied is invalid";
        }
        if (validateState === VoterValidateResult.PersonalIdInvalid) {
            const label = ballot?.personalIdLabel || "Personal ID";
            error = `The ${label} supplied is invalid`;
        }
        setAuthError(error);
        setIsAuthInit(true);
    };

    const handleUpdateAuth = (onComplete = null) => {
        authVoter(voterId, voterGuid, receipt, personalId, previewId, onComplete);
    };

    const onValidateReceipt = (receiptVal, onComplete) => {
        setReceipt(receiptVal);
        authVoter(voterId, voterGuid, receiptVal, personalId, previewId, onComplete);
    };

    const onUpdateReceipt = (receiptVal) => {
        setReceipt(receiptVal);
    };

    const onValidatePersonalId = (personalIdVal, onComplete) => {
        setPersonalId(personalIdVal);
        authVoter(voterId, voterGuid, receipt, personalIdVal, previewId, onComplete);
    };

    const onValidateLogin = (voterIdVal, password, onComplete) => {
        setVoterId(voterIdVal);
        axios.post('/auth/login', { id: voterIdVal, password })
            .then(response => {
                if (response.data.isSuccessful) {
                    const voter = response.data.payload.voter;
                    window.location.search = `id=${voter.uniqueId}&key=${voter.guid}`;
                } else {
                    setAuthError(response.data.errorMessage);
                }
                onComplete();
            })
            .catch(alert);
    };

    const renderContent = () => {
        if (pageError) {
            return <div className="mt-8"><ErrorBanner>{pageError}</ErrorBanner></div>;
        } else if (isAuthLogin || isAuthReceipt || isAuthPersonalId) {
            return (
                <Auth
                    isAuthReceipt={isAuthReceipt}
                    isAuthPersonalId={isAuthPersonalId}
                    authError={authError}
                    onValidateReceipt={onValidateReceipt}
                    onValidatePersonalId={onValidatePersonalId}
                    onValidateLogin={onValidateLogin}
                />
            );
        } else if (voter && ballot) {
            return (
                <div className="bg-white py-8 px-10 shadow sm:rounded-lg sm:mt-8">
                    <Vote
                        receipt={receipt}
                        personalId={personalId}
                        validateState={validateState}
                        isPreview={isPreview}
                        previewId={previewId}
                        bioId={bioId}
                        onUpdateAuth={handleUpdateAuth}
                        setReceipt={onUpdateReceipt}
                        isInfoPageUrl={isInfoPageUrl}
                    />
                </div>
            );
        }
        return null;
    };

    return (
        <div className="container" role="main">
            <LoadingPanel
                className="bg-white py-8 px-10 shadow sm:rounded-lg sm:mt-8 text-center"
                isLoading={!isAuthInit}
                loadingText="Loading Ballot..."
            />
            {renderContent()}
            <Footer />
        </div>
    );
}